import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { connect } from "react-redux";
import {
  getHelpdeskMessage,
  updateHelpdeskMessage,
} from "../../store/actions";

const validationSchema = Yup.object().shape({
  chrome_new_connection_auto_test: Yup.boolean(),
  chrome_test_on_startup: Yup.boolean(),
  chrome_daily_auto_test: Yup.boolean(),
  extension_notification: Yup.boolean(),
  store_email: Yup.boolean(),
  last_restart_time_threshold: Yup.number(),
  test_frequency_hours: Yup.number(),
});

const ExtensionSettings = ({
  dispatchGetHelpdeskMessage,
  dispatchUpdateHelpdeskMessage,
  helpdeskMessage,
  isHelpdeskMessageLoading,
}) => {
  const {
    chrome_new_connection_auto_test,
    chrome_test_on_startup,
    chrome_daily_auto_test,
    extension_notification,
    store_email,
    last_restart_time_threshold,
    test_frequency_hours,
  } = helpdeskMessage || {};

  const formik = useFormik({
    initialValues: {
      chrome_new_connection_auto_test: chrome_new_connection_auto_test || false,
      chrome_test_on_startup: chrome_test_on_startup || false,
      chrome_daily_auto_test: chrome_daily_auto_test || false,
      extension_notification: extension_notification || false,
      store_email: store_email || false,
      last_restart_time_threshold: last_restart_time_threshold || 2,
      test_frequency_hours: test_frequency_hours || 24
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      try {
        dispatchUpdateHelpdeskMessage(values);
      } catch (error) {
        // Handle errors
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    dispatchGetHelpdeskMessage();
  }, [dispatchGetHelpdeskMessage]);

  const checkboxSettings = [
    {
      id: "chrome_new_connection_auto_test",
      label: "Auto-Test New Networks",
      description:
        "Run a speed test automatically when a new internet connection is detected",
    },
    {
      id: "chrome_test_on_startup",
      label: "Auto-Test on Startup",
      description:
        "Run a speed test automatically each time the extension starts",
    },
    {
      id: "chrome_daily_auto_test",
      label: "Recurring testing",
      description: "Trigger a network test if the most recent results are over.",
    },
    {
      id: "extension_notification",
      label: "Device Restart Notification",
      description:
        "Remind users to restart their devices to maintain optimal performance.",
    },
    {
      id: "store_email",
      label: "Storing User Email",
      description: "Only available for Extension V3.",
    },
    // {
    //   id: "packet_loss_testing",
    //   label: "Enable Packet Loss Testing",
    //   description: "Toggle to enable or disable packet loss testing.",
    // },
    // {
    //   id: "hop_to_hop_testing",
    //   label: "Enable Hop-to-Hop Testing",
    //   description: "Toggle to enable or disable hop-to-hop testing.",
    // },
  ];

  return (
    <Card className="border rounded-lg shadow-sm">
      <CardHeader className="flex flex-row items-center gap-2">
        <div>
          <CardTitle className="text-xl font-bold">Extension Testing Settings</CardTitle>
          <CardDescription>
            Configure testing behavior and diagnostic features.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-4">
            {checkboxSettings.map(({ id, label, description }) => (
              <div key={id} className="bg-white p-4 rounded-md border border-gray-100">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id={id}
                    checked={formik.values[id]}
                    onCheckedChange={(checked) => {
                      formik.setFieldValue(id, checked);
                      if (!checked) {
                        if (id === "chrome_daily_auto_test") {
                          formik.setFieldValue("test_frequency_hours", 0);
                        }
                        if (id === "extension_notification") {
                          formik.setFieldValue("last_restart_time_threshold", 0);
                        }
                      }
                    }}
                    className="mt-1"
                  />
                  <div>
                    <label htmlFor={id} className="font-medium text-gray-900">
                      {label}
                    </label>
                    <p className="text-sm text-gray-600">{description}</p>
                  </div>
                </div>

                {id === "chrome_daily_auto_test" && formik.values.chrome_daily_auto_test && (
                  <div className="mt-3 ml-8">
                    <label
                      htmlFor="test_frequency_hours"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Trigger a network test if the most recent results are over (hours)
                    </label>
                    <Input
                      id="test_frequency_hours"
                      name="test_frequency_hours"
                      type="number"
                      min={1}
                      value={formik.values.test_frequency_hours}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="mt-1 w-16"
                    />
                    {formik.touched.test_frequency_hours &&
                      formik.errors.test_frequency_hours && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.test_frequency_hours}
                        </p>
                      )}
                  </div>
                )}

                {id === "extension_notification" && formik.values.extension_notification && (
                  <div className="mt-3 ml-8">
                    <label
                      htmlFor="last_restart_time_threshold"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Notify user weekly if their device hasn't been restarted in over (days)
                    </label>
                    <Input
                      id="last_restart_time_threshold"
                      name="last_restart_time_threshold"
                      type="number"
                      min={1}
                      value={formik.values.last_restart_time_threshold}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="mt-1 w-16"
                    />
                    {formik.touched.last_restart_time_threshold &&
                      formik.errors.last_restart_time_threshold && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.last_restart_time_threshold}
                        </p>
                      )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="pt-4 mt-4 border-t border-gray-100">
            <Button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={formik.isSubmitting}
            >
              Save settings
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

ExtensionSettings.propTypes = {
  dispatchUpdateHelpdeskMessage: PropTypes.func.isRequired,
  isHelpdeskMessageLoading: PropTypes.bool.isRequired,
  helpdeskMessage: PropTypes.shape({
    chrome_daily_auto_test: PropTypes.bool,
    chrome_test_on_startup: PropTypes.bool,
    last_restart_time_threshold: PropTypes.number,
    test_frequency_hours: PropTypes.number,
    extension_notification: PropTypes.bool,
    store_email: PropTypes.bool,
    packet_loss_testing: PropTypes.bool,
    hop_to_hop_testing: PropTypes.bool,
  }),
};

const mapStateToProps = (state) => {
  const { isHelpdeskMessageLoading, helpdeskMessage = {} } = state.EXConfig;
  return { isHelpdeskMessageLoading, helpdeskMessage };
};

const mapDispatchToProps = {
  dispatchGetHelpdeskMessage: getHelpdeskMessage,
  dispatchUpdateHelpdeskMessage: updateHelpdeskMessage,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExtensionSettings);
